/**
 * Config-generator schemas — templates and renderable input fields.
 *
 * A template = a curated config snippet (or whole device config) that the
 * user fills in via a dynamic form. The fields[] array drives a
 * schema-driven form so adding a new template doesn't require new UI code.
 */
import { z } from "zod";

/** Field types the dynamic form runner knows how to render. */
const TemplateFieldTypeSchema = z.enum([
  "text",     // single-line input
  "number",   // number input
  "select",   // <select> with options
  "textarea", // multi-line
  "boolean",  // checkbox
]);
export type TemplateFieldType = z.infer<typeof TemplateFieldTypeSchema>;

const TemplateFieldSchema = z.object({
  /** Programmatic key inserted into the rendered config (matches `{{name}}`). */
  name: z.string(),
  /** Human label shown above the input. */
  label: z.string(),
  type: TemplateFieldTypeSchema,
  /** Default value rendered into the input on first paint. */
  defaultValue: z.union([z.string(), z.number(), z.boolean()]).optional(),
  /** For type="select", the available options. */
  options: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
  /** Optional helper text shown under the input. */
  hint: z.string().optional(),
  /** True when the field must be non-empty. */
  required: z.boolean().optional(),
});
export type TemplateField = z.infer<typeof TemplateFieldSchema>;

const ConfigTemplateSchema = z.object({
  slug: z.string(),
  name: z.string(),
  vendor: z.enum(["meraki", "aruba", "shasta", "system"]),
  category: z.string(),
  description: z.string(),
  /** The body of the template. `{{field_name}}` placeholders get substituted. */
  body: z.string(),
  /** Form fields whose values populate the body's placeholders. */
  fields: z.array(TemplateFieldSchema),
  updatedAt: z.string().optional(),
  /** True for GuestTek-blessed templates. */
  blessed: z.boolean().optional(),
});
export type ConfigTemplate = z.infer<typeof ConfigTemplateSchema>;
export const ConfigTemplateListSchema = z.array(ConfigTemplateSchema);
